// import polyfills first
import 'core-js/stable'
import axios from 'axios'
import moment from 'moment'
import { fetchAppConfiguration, AppConfigurationPlugin } from '@/helpers/app-config-helper'
import cssVars from 'css-vars-ponyfill'
import Vue from 'vue'
import Vuex from 'vuex'
import VueRouter from 'vue-router'
import VueKeycloakPlugin from '@/plugins/keycloak-plugin'
import Vuetify from 'vuetify/lib'

import VueAxios from 'vue-axios'
import VueLayers from 'vuelayers'
import DateFormatFilter from '@/filters/date-format'
import OrDefaultFilter from '@/filters/or-default'
import UppercaseFilter from '@/filters/uppercase'
import PrettyBytesFilter from '@/filters/pretty-bytes-filter'
import PercentGroup from '@/components/form-fields/PercentGroup'
import RemoteSelect from '@/components/form-fields/RemoteSelect'
import AppButton from '@/components/common/AppButton'
import AppButtonCancel from '@/components/common/AppButtonCancel'
import AppButtonRound from '@/components/common/AppButtonRound'
import AppButtonUpload from '@/components/common/AppButtonUpload'
import AppChip from '@/components/common/AppChip'
import AppDatePicker from '@/components/common/AppDatePicker'
import AppDateSelector from '@/components/common/AppDateSelector'
import AppDateRangePicker from '@/components/common/AppDateRangePicker'
import AppDragHandle from '@/components/common/AppDragHandle'
import AppDraggable from '@/components/common/AppDraggable'
import AppDraggableItem from '@/components/common/AppDraggableItem'
import AppFilterDialog from '@/components/common/AppFilterDialog'
import AppFilterList from '@/components/common/AppFilterList'
import AppFind from '@/components/common/AppFind'
import AppIcon from '@/components/common/AppIcon'
import AppInfoField from '@/components/common/AppInfoField'
import AppPatientAutocomplete from '@/components/common/AppPatientAutocomplete'
import AppPatientCreateEditDialog from '@/components/common/AppPatientCreateEditDialog'
import AppPdf from '@/components/common/AppPdf'
import AppNotificationBubble from '@/components/common/AppNotificationBubble'
import AppFieldToggle from '@/components/common/AppFieldToggle'
import AppReminderDialog from '@/components/common/AppReminderDialog'
import AppReminderNotificationContainer from '@/components/common/AppReminderNotificationContainer'
import AppReminderCard from '@/components/common/AppReminderCard'
import AppToggle from '@/components/common/AppToggle'
import AppTooltip from '@/components/common/AppTooltip'
import AppTruncateText from '@/components/common/AppTruncateText'
import Autocomplete from '@/components/form-fields/Autocomplete'
import TextField from '@/components/form-fields/TextField'
import TextArea from '@/components/form-fields/TextArea'
import Radios from '@/components/form-fields/Radios'
import FieldGroup from '@/components/form-fields/FieldGroup'
import Vuelidate from 'vuelidate'
import {
  setMyActiveOrganization,
  initializeMyAccount,
  MyActiveOrganization,
  MyActiveRoles,
  MyUser
} from '@/helpers/my-account-helper'
import VueHotkey from 'v-hotkey'
import { configureApiTokenInterceptor } from '@/services/viper-api'
import { keycloakService, ROLE_USER } from '@/services/keycloak-service'
import { initializeClinicalApplication } from '@/init'
import { AppParams } from '@/config/app-params'
import { configureSentryContext, initSentry } from '@/config/sentry-config'
import { KeycloakConfig } from '@/config/keycloak-config'
import { configureSessionTimeout } from '@/config/timeout-config'
import { initDatadog } from '@/config/datadog-config'

const start = moment()
async function main() {
  const appConfig = await fetchAppConfiguration()
  const appParams = new AppParams(document.location.search)
  window.$appConfig = appConfig
  window.$appParams = appParams

  initSentry(appParams.env)
  initDatadog(appParams.env)

  const keycloakConfig = new KeycloakConfig(appParams.idp, async () => {
    const mountingElement = '#app'

    if (appParams.tenant) {
      setMyActiveOrganization({}, appParams.tenant)
    }

    configureSessionTimeout(appParams)
    configureApiTokenInterceptor()
    const initialized = await initializeMyAccount()

    configureSentryContext(MyUser, MyActiveOrganization, MyActiveRoles)

    if (initialized && keycloakService.hasRole(ROLE_USER)) {
      initializeClinicalApplication(mountingElement)
    } else if (initialized) {
      // TODO: there's definitely a better way to handle this
      console.warn('user does not have VIPER role')
      keycloakService.logout()
    } else {
      // no membership page
      initializeClinicalApplication(mountingElement)
    }
  })

  cssVars({ watch: true })
  Vue.use(AppConfigurationPlugin(appConfig))
  Vue.use(VueKeycloakPlugin, keycloakConfig)
  Vue.use(Vuelidate)
  Vue.use(Vuex)
  Vue.use(VueRouter)
  Vue.use(Vuetify)
  Vue.use(VueLayers)
  Vue.use(VueAxios, axios)
  Vue.use(VueHotkey)

  Vue.filter('dateFormat', DateFormatFilter)
  Vue.filter('prettyBytes', PrettyBytesFilter)
  Vue.filter('orDefault', OrDefaultFilter)
  Vue.filter('uppercase', UppercaseFilter)

  Vue.component('field-viper-field-option-select', RemoteSelect)
  Vue.component('field-viper-text-field', TextField)
  Vue.component('field-viper-text-area', TextArea)
  Vue.component('field-viper-radios', Radios)
  Vue.component('field-viper-field-autocomplete', Autocomplete)
  Vue.component('field-group', FieldGroup)
  Vue.component('percent-field-group', PercentGroup)
  Vue.component('app-button', AppButton)
  Vue.component('app-button-cancel', AppButtonCancel)
  Vue.component('app-button-round', AppButtonRound)
  Vue.component('app-button-upload', AppButtonUpload)
  Vue.component('app-chip', AppChip)
  Vue.component('app-date-picker', AppDatePicker)
  Vue.component('app-date-range-picker', AppDateRangePicker)
  Vue.component('app-date-selector', AppDateSelector)
  Vue.component('app-drag-handle', AppDragHandle)
  Vue.component('app-draggable', AppDraggable)
  Vue.component('app-draggable-item', AppDraggableItem)
  Vue.component('app-field-toggle', AppFieldToggle)
  Vue.component('app-filter-dialog', AppFilterDialog)
  Vue.component('app-filter-list', AppFilterList)
  Vue.component('app-find', AppFind)
  Vue.component('app-icon', AppIcon)
  Vue.component('app-notification-bubble', AppNotificationBubble)
  Vue.component('app-info-field', AppInfoField)
  Vue.component('app-patient-autocomplete', AppPatientAutocomplete)
  Vue.component('app-patient-create-edit-dialog', AppPatientCreateEditDialog)
  Vue.component('app-pdf', AppPdf)
  Vue.component('app-reminder-dialog', AppReminderDialog)
  Vue.component('app-reminder-notification-container', AppReminderNotificationContainer)
  Vue.component('app-reminder-card', AppReminderCard)
  Vue.component('app-tooltip', AppTooltip)
  Vue.component('app-toggle', AppToggle)
  Vue.component('app-truncate-text', AppTruncateText)
}

main().then(() => {
  const startTime = moment().diff(start)
  console.info(`app init complete in ${startTime} millis`)
})
